import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { SwipeableNotification } from "@/components/notifications/SwipeableNotification";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Bell, RefreshCw, Trash2, Check, ShieldCheck, CalendarDays, MessageSquare, Package, AlertTriangle, CreditCard, FileText, ClipboardList, LogOut } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type NotificationFilter = "all" | "unread";

interface Notification {
  id: string;
  message: string;
  type: string;
  read: boolean;
  created_at: string;
  action: string | null;
  executor_name: string | null;
  event_at: string | null;
}

const sanitizeText = (value?: string | null) => {
  const normalized = value?.trim();
  return normalized ? normalized : null;
};

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  login: "Acesso ao sistema",
  logout: "Saída do sistema",
  agendamento_criado: "Novo agendamento",
  agendamento_cancelado: "Agendamento cancelado",
  agendamento_editado: "Agendamento reagendado",
  agendamento_status_alterado: "Status alterado",
  estoque_baixo: "Itens com estoque baixo",
  paciente_cadastrado: "Novo paciente",
  pagamento_registrado: "Pagamento registrado",
  avaliacao_fisio: "Avaliação registrada",
  recibo_emitido: "Recibo emitido",
  mensagem_chat: "Mensagem no chat",
  pacote_proximo_fim: "Pacote próximo do fim",
  mensalidade_proxima_fim: "Mensalidade próxima do fim",
  dashboard_alert: "Alerta do painel",
};

const NOTIFICATION_TYPE_EMOJIS: Record<string, string> = {
  agendamento_criado: "✅",
  agendamento_cancelado: "❌",
  agendamento_editado: "🔄",
  pacote_proximo_fim: "📦",
  mensalidade_proxima_fim: "🎉",
  estoque_baixo: "⚠️",
  mensagem_chat: "💬",
};

const getNotificationTitle = (notification: Notification) => {
  const executor = sanitizeText(notification.executor_name);
  const action = sanitizeText(notification.action);
  const label = action ?? NOTIFICATION_TYPE_LABELS[notification.type] ?? "Notificação";
  const emoji = NOTIFICATION_TYPE_EMOJIS[notification.type];
  const decoratedLabel = emoji ? `${emoji} ${label}` : label;
  if (executor) return `${executor} · ${decoratedLabel}`;
  return decoratedLabel;
};

const getNotificationBody = (notification: Notification) => {
  return sanitizeText(notification.message);
};

const NotificationItem = ({ notification }: { notification: Notification }) => {
  const title = getNotificationTitle(notification);
  const body = getNotificationBody(notification);

  return (
    <div className="space-y-0.5">
      <strong className="text-sm leading-snug font-medium block">{title}</strong>
      {body && (
        <p className="text-sm leading-snug text-muted-foreground">{body}</p>
      )}
    </div>
  );
};

const formatNotificationDate = (value?: string | null) => {
  if (!value) return "não informado";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "não informado";

  return format(parsedDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
};

const formatNotificationDistance = (value?: string | null) => {
  if (!value) return "há instantes";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "há instantes";

  return formatDistanceToNow(parsedDate, { addSuffix: true, locale: ptBR });
};

const chunkArray = <T,>(items: T[], chunkSize: number) => {
  const chunks: T[][] = [];

  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }

  return chunks;
};

const getClearedNotificationsStorageKey = (userId?: string) =>
  userId ? `notification-bell-cleared:${userId}` : null;

const loadClearedNotificationIds = (userId?: string) => {
  const storageKey = getClearedNotificationsStorageKey(userId);
  if (!storageKey) return new Set<string>();

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return new Set<string>();

    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return new Set<string>();

    return new Set(parsed.filter((value): value is string => typeof value === "string"));
  } catch {
    return new Set<string>();
  }
};

const persistClearedNotificationIds = (userId: string, notificationIds: Set<string>) => {
  const storageKey = getClearedNotificationsStorageKey(userId);
  if (!storageKey) return;

  window.localStorage.setItem(storageKey, JSON.stringify(Array.from(notificationIds)));
};

const markNotificationsAsReadInChunks = async (notificationIds: string[], chunkSize = 10) => {
  if (notificationIds.length === 0) return;

  const chunks = chunkArray(notificationIds, chunkSize);

  const results = await Promise.all(
    chunks.map(async (chunk) =>
      supabase
        .from("notifications")
        .update({ read: true })
        .in("id", chunk),
    ),
  );

  const firstError = results.find((result) => result.error)?.error;
  if (firstError) {
    throw firstError;
  }
};

export default function NotificationBell() {
  const { isMaster, user } = useAuth();
  const isMobile = useIsMobile();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [swipeHintShown, setSwipeHintShown] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>("all");
  const [updatingReadState, setUpdatingReadState] = useState(false);
  const [clearConfirming, setClearConfirming] = useState(false);
  const [clearingNotifications, setClearingNotifications] = useState(false);
  const [clearedNotificationIds, setClearedNotificationIds] = useState<Set<string>>(new Set());

  const visibleNotifications = useMemo(
    () => notifications.filter((notification) => !clearedNotificationIds.has(notification.id)),
    [notifications, clearedNotificationIds],
  );

  const unreadCount = useMemo(() => visibleNotifications.filter((n) => !n.read).length, [visibleNotifications]);

  const filteredNotifications = useMemo(
    () => visibleNotifications.filter((notification) => (activeFilter === "unread" ? !notification.read : true)),
    [visibleNotifications, activeFilter],
  );

  useEffect(() => {
    setClearedNotificationIds(loadClearedNotificationIds(user?.id));
  }, [user?.id]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      setNotifications([]);
      setLoadError(error.message);
      console.error("[NotificationBell] Falha ao carregar notificações", {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        userId: user?.id ?? null,
        isMaster,
      });
      setLoading(false);
      return;
    }

    const loadedNotifications = (data ?? []) as unknown as Notification[];
    setNotifications(loadedNotifications);

    if (user?.id) {
      setClearedNotificationIds((previousCleared) => {
        if (previousCleared.size === 0) return previousCleared;

        const validIds = new Set(loadedNotifications.map((notification) => notification.id));
        const nextCleared = new Set(
          Array.from(previousCleared).filter((notificationId) => validIds.has(notificationId)),
        );

        if (nextCleared.size !== previousCleared.size) {
          persistClearedNotificationIds(user.id, nextCleared);
          return nextCleared;
        }

        return previousCleared;
      });
    }

    setLoading(false);
  }, [isMaster, user?.id]);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          const incoming = payload.new as Notification;
          setNotifications((prev) => {
            const alreadyExists = prev.some((notification) => notification.id === incoming.id);
            if (alreadyExists) return prev;
            return [incoming, ...prev].slice(0, 50);
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications" },
        (payload) => {
          const updated = payload.new as Notification;
          setNotifications((prev) =>
            prev.map((notification) =>
              notification.id === updated.id ? { ...notification, ...updated } : notification,
            ),
          );
        },
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.error("[NotificationBell] Canal realtime com erro", {
            userId: user?.id ?? null,
            isMaster,
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications, user?.id]);

  useEffect(() => {
    if (!open) {
      setClearConfirming(false);
    }
  }, [open]);

  const markAsRead = useCallback(async (notificationId: string) => {
    const targetNotification = notifications.find((notification) => notification.id === notificationId);
    if (!targetNotification || targetNotification.read) return;

    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId ? { ...notification, read: true } : notification,
      ),
    );

    try {
      setUpdatingReadState(true);
      await markNotificationsAsReadInChunks([notificationId], 1);
    } catch (error) {
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId ? { ...notification, read: targetNotification.read } : notification,
        ),
      );
      console.error("[NotificationBell] Failed to mark a notification as read:", error);
    } finally {
      setUpdatingReadState(false);
    }
  }, [notifications]);

  const markAllRead = useCallback(async () => {
    const unreadIds = visibleNotifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;

    const unreadSet = new Set(unreadIds);
    setNotifications((prev) =>
      prev.map((notification) =>
        unreadSet.has(notification.id) ? { ...notification, read: true } : notification,
      ),
    );

    try {
      setUpdatingReadState(true);
      await markNotificationsAsReadInChunks(unreadIds);
    } catch (error) {
      setNotifications((prev) =>
        prev.map((notification) =>
          unreadSet.has(notification.id) ? { ...notification, read: false } : notification,
        ),
      );
      console.error("[NotificationBell] Failed to mark all notifications as read:", error);
    } finally {
      setUpdatingReadState(false);
    }
  }, [visibleNotifications]);

  const clearAllNotifications = useCallback(async () => {
    if (visibleNotifications.length === 0 || !user?.id) return;

    try {
      setClearingNotifications(true);

      const nextClearedIds = new Set(clearedNotificationIds);
      visibleNotifications.forEach((notification) => {
        nextClearedIds.add(notification.id);
      });

      setClearedNotificationIds(nextClearedIds);
      persistClearedNotificationIds(user.id, nextClearedIds);
      setClearConfirming(false);
    } catch (error) {
      console.error("[NotificationBell] Failed to clear all notifications:", error);
    } finally {
      setClearingNotifications(false);
    }
  }, [clearedNotificationIds, user?.id, visibleNotifications]);

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Abrir notificações">
          <Bell className={cn("h-4 w-4", unreadCount > 0 && "animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]")} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 rounded-full bg-destructive px-1 text-destructive-foreground text-[10px] font-bold flex items-center justify-center animate-[scale-in_0.2s_ease-out]">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          <span className="sr-only">{unreadCount} notificações não lidas</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(360px,calc(100vw-1rem))] max-h-[min(80vh,640px)] overflow-hidden p-0"
        align="end"
        sideOffset={10}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h4 className="font-semibold text-sm">Notificações</h4>
            <p className="text-xs text-muted-foreground">Acompanhe eventos importantes em tempo real</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={fetchNotifications}
            disabled={loading}
            aria-label="Atualizar notificações"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>

        <div className="px-4 py-2 border-b flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex flex-wrap rounded-lg border bg-muted/40 p-1">
            <Button
              type="button"
              size="sm"
              variant={activeFilter === "all" ? "secondary" : "ghost"}
              className="h-7 px-3 text-xs"
              onClick={() => setActiveFilter("all")}
            >
              Todas ({visibleNotifications.length})
            </Button>
            <Button
              type="button"
              size="sm"
              variant={activeFilter === "unread" ? "secondary" : "ghost"}
              className="h-7 px-3 text-xs"
              onClick={() => setActiveFilter("unread")}
            >
              Não lidas ({unreadCount})
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-1 sm:justify-end">
            {visibleNotifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 text-destructive hover:text-destructive"
                onClick={() => setClearConfirming((prev) => !prev)}
                disabled={updatingReadState || clearingNotifications}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Limpar
              </Button>
            )}

            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={markAllRead}
                disabled={updatingReadState || clearingNotifications}
              >
                <Check className="mr-1 h-3 w-3" />
                Todas lidas
              </Button>
            )}
          </div>
        </div>

        {clearConfirming && visibleNotifications.length > 0 && (
          <div className="px-4 py-2 border-b bg-destructive/5 flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">Deseja limpar todas as notificações?</p>
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                className="h-7 px-2 text-[11px]"
                onClick={clearAllNotifications}
                disabled={clearingNotifications}
              >
                SIM
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-[11px]"
                onClick={() => setClearConfirming(false)}
                disabled={clearingNotifications}
              >
                NÃO
              </Button>
            </div>
          </div>
        )}

        <ScrollArea className="h-[min(420px,calc(80vh-170px))]">
          {loading ? (
            <p className="text-center text-sm text-muted-foreground py-8">Carregando notificações...</p>
          ) : loadError ? (
            <div className="px-4 py-4 space-y-2">
              <p className="text-sm text-destructive font-medium">Erro ao carregar notificações.</p>
              <p className="text-xs text-muted-foreground break-words">{loadError}</p>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={fetchNotifications}>
                Tentar novamente
              </Button>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="py-10 px-6 text-center">
              <p className="text-sm font-medium">Nenhuma notificação encontrada</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {activeFilter === "unread"
                  ? "Você já está em dia. Não há notificações não lidas."
                  : "Novos eventos aparecerão aqui automaticamente."}
              </p>
            </div>
          ) : (
            filteredNotifications.map((n, index) => {
              const isFirstUnread = !n.read && index === filteredNotifications.findIndex((x) => !x.read);

              const content = (
                <div
                  className={cn(
                    "px-4 py-3 border-b last:border-0 transition-colors",
                    !n.read && "bg-primary/5",
                  )}
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5",
                        n.type === "login" || n.type === "logout"
                          ? "bg-primary/15 text-primary"
                          : n.type === "mensagem_chat"
                            ? "bg-info/15 text-info"
                            : n.type === "estoque_baixo"
                              ? "bg-warning/15 text-warning"
                              : n.type === "pagamento_registrado" || n.type === "recibo_emitido"
                                ? "bg-success/15 text-success"
                                : "bg-service-fisioterapia/15 text-accent-foreground",
                      )}
                    >
                      {n.type === "login" ? <ShieldCheck className="h-4 w-4" /> :
                       n.type === "logout" ? <LogOut className="h-4 w-4" /> :
                       n.type === "mensagem_chat" ? <MessageSquare className="h-4 w-4" /> :
                       n.type === "pacote_proximo_fim" ? <Package className="h-4 w-4" /> :
                       n.type === "mensalidade_proxima_fim" ? <Package className="h-4 w-4" /> :
                       n.type === "estoque_baixo" ? <AlertTriangle className="h-4 w-4" /> :
                       n.type === "pagamento_registrado" ? <CreditCard className="h-4 w-4" /> :
                       n.type === "recibo_emitido" ? <FileText className="h-4 w-4" /> :
                       n.type === "avaliacao_fisio" ? <ClipboardList className="h-4 w-4" /> :
                       <CalendarDays className="h-4 w-4" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <NotificationItem notification={n} />
                        {!n.read && !isMobile && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => markAsRead(n.id)}
                            disabled={updatingReadState}
                          >
                            <Check className="mr-1 h-3 w-3" />
                            Lida
                          </Button>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mt-1">
                        {formatNotificationDistance(n.created_at)} · {formatNotificationDate(n.event_at ?? n.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );

              return (
                <SwipeableNotification
                  key={n.id}
                  enabled={isMobile && !n.read}
                  showHint={isFirstUnread && !swipeHintShown}
                  onSwipeComplete={() => {
                    setSwipeHintShown(true);
                    markAsRead(n.id);
                  }}
                >
                  {content}
                </SwipeableNotification>
              );
            })
          )}
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
